import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web/build/player/lottie_svg';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import { authInterceptor } from './core/interceptors/auth-interceptor';
import { globalLoadingInterceptor } from './core/interceptors/global-loading.interceptor';
import { LucideAngularModule, Plus, Trash2, Copy, ArrowRight, Download } from 'lucide-angular';
import { adminAuthInterceptor } from './core/interceptors/admin-auth-interceptor';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { provideToastr } from 'ngx-toastr';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { reducers } from './store';
import { UsersEffects } from './store/users/users.effects';
import { BrokersEffects } from './store/brokers/brokers.effects';
import { TicketsEffects } from './store/tickets/tickets.effects';
import { LogsEffects } from './store/logs/logs.effects';
import { HealthEffects } from './store/health/health.effects';
import { FirebaseService } from './core/firebase/firebase.service';
import { NotificationService } from './core/firebase/notification.service';

export const appConfig: ApplicationConfig = {
  providers: [
    // FCM: initialize Firebase and foreground push listener before the app renders.
    provideAppInitializer(async () => {
      inject(FirebaseService).initialize();
      await inject(NotificationService).startForegroundMessageListener();
    }),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(
      withInterceptors([globalLoadingInterceptor, authInterceptor, adminAuthInterceptor]),
    ),
    provideLottieOptions({
      player: () => player,
    }),
    provideNgxSkeletonLoader({
      theme: {
        extendsFromRoot: true,
        ['--loader-border-radius']: '8px',
      },
    }),
    provideStore(reducers),
    provideEffects([UsersEffects, BrokersEffects, TicketsEffects, LogsEffects, HealthEffects]),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !environment.production,
    }),
    importProvidersFrom(
      LucideAngularModule.pick({ Trash2, Plus, Copy, ArrowRight, Download }),
      MatMenuModule,
      MatIconModule,
      MatButtonModule,
      MatDividerModule,
    ),
    provideAnimationsAsync(),
    provideToastr({
      timeOut: 5000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      closeButton: true,
      progressBar: false,
      enableHtml: false,
      tapToDismiss: true,
      newestOnTop: true,
      maxOpened: 5,
      toastClass: 'toast ngx-toastr',
      titleClass: 'toast-title',
      messageClass: 'toast-message',
    }),
  ],
};
